import React from "react";

import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";

import { displayEdit, buildEmptyObject } from "@auto/widgets/EditComponent";

import { rest, t } from "@auto/services";
import { getServiceUri } from "@auto/metadata";
import { can } from "@src/security/permissions";

import styles from "./PersonForm.module.styl";

const RELATED_LIMIT = 5000;

const formatDate = (value) => {
  if (!value) {
    return "";
  }

  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }

  try {
    return new Date(value).toISOString().slice(0, 10);
  } catch {
    return String(value);
  }
};

const formatDateTime = (value) => {
  if (!value) {
    return "";
  }

  if (typeof value === "string") {
    return value.replace("T", " ").slice(0, 19);
  }

  try {
    return new Date(value).toISOString().replace("T", " ").slice(0, 19);
  } catch {
    return String(value);
  }
};

const fullName = (person) => {
  return `${person?.firstName || ""} ${person?.lastName || ""}`.trim();
};

const initials = (firstName, lastName) => {
  const first = (firstName || "").trim();
  const last = (lastName || "").trim();

  const value = `${first ? first[0].toUpperCase() : ""}${last ? last[0].toUpperCase() : ""}`;

  return value || "?";
};

const isPrimary = (item) => {
  return item?.isPrimary === true || item?.is_primary === true;
};

const isDefaultPhoto = (photo) => {
  return photo?.isDefault === true || photo?.is_default === true;
};

const isSubmittedStatus = (value) => {
  return value === 1 || value === "1";
};

const photoToAvatarSrc = (photo) => {
  if (!photo?.id) {
    return "";
  }

  return `${getServiceUri()}person-photo/data/${photo.id}`;
};

const search = async (entity, payload) => {
  return await rest.request(
    getServiceUri() + `${entity}/search`,
    "POST",
    payload
  );
};

const searchOneByGid = async (entity, gid) => {
  if (!gid) {
    return null;
  }

  const rows = await search(entity, {
    columns: [],
    query: `gid==${gid}`,
    limit: 1,
    offset: 0,
  });

  return rows?.[0] || null;
};

const searchByPersonGid = async (entity, personGid) => {
  if (!personGid) {
    return [];
  }

  return await search(entity, {
    columns: [],
    query: `personGid==${personGid}`,
    limit: RELATED_LIMIT,
    offset: 0,
  });
};

const pickPrimaryPhoto = (photos) => {
  if (!photos || photos.length === 0) {
    return null;
  }

  return photos.find(isDefaultPhoto) || photos[0];
};

const computeHeaderRegistrationLabel = (voters, applications) => {
  if ((voters || []).length > 0) {
    return t("person.registered");
  }

  const latestApplication = [...(applications || [])].sort((a, b) => {
    const av = a.submittedAt ?? a.submitted_at ?? "";
    const bv = b.submittedAt ?? b.submitted_at ?? "";

    return String(bv).localeCompare(String(av));
  })[0];

  if (
    latestApplication &&
    isSubmittedStatus(latestApplication.statusCode ?? latestApplication.status_code)
  ) {
    return t("person.applicationSubmitted");
  }

  return t("person.notRegistered");
};

const enrichPerson = async (person, gui) => {
  if (!person?.gid) {
    return person || {};
  }

  const personGid = person.gid;

  const canSearchContacts = can(gui, "SEARCH_PERSON_CONTACT");
  const canSearchResidences = can(gui, "SEARCH_PERSON_RESIDENCE");
  const canSearchPhotos = can(gui, "SEARCH_PERSON_PHOTO");
  const canSearchVoters = can(gui, "SEARCH_VOTER");
  const canSearchApplications = can(gui, "SEARCH_VOTER_APPLICATION");

  const [contacts, residences, photos, voters, applications] = await Promise.all([
    canSearchContacts ? searchByPersonGid("person-contact", personGid) : Promise.resolve([]),
    canSearchResidences ? searchByPersonGid("person-residence", personGid) : Promise.resolve([]),
    canSearchPhotos ? searchByPersonGid("person-photo", personGid) : Promise.resolve([]),
    canSearchVoters ? searchByPersonGid("voter", personGid) : Promise.resolve([]),
    canSearchApplications ? searchByPersonGid("voter-application", personGid) : Promise.resolve([]),
  ]);

  const primaryPhoto = pickPrimaryPhoto(photos);

  return {
    ...person,

    fullName: fullName(person),

    contacts: contacts || [],
    residences: residences || [],
    photos: photos || [],
    voters: voters || [],
    applications: applications || [],

    primaryPhoto,
    avatarSrc: can(gui, "READ_PERSON_PHOTO") ? photoToAvatarSrc(primaryPhoto) : "",
    headerRegistrationLabel:
      canSearchVoters && canSearchApplications
        ? computeHeaderRegistrationLabel(voters, applications)
        : "",
  };
};

const SectionCard = ({ title, children }) => {
  return (
    <section className={styles.card}>
      <header className={styles.cardHeader}>
        <h2>{title}</h2>
      </header>

      <div className={styles.cardBody}>
        {children}
      </div>
    </section>
  );
};

const Field = ({ label, value }) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  return (
    <div className={styles.field}>
      <div className={styles.fieldLabel}>
        {label}
      </div>

      <div className={styles.fieldValue}>
        {String(value)}
      </div>
    </div>
  );
};

const Badge = ({ children, variant = "default" }) => {
  return (
    <span className={`${styles.badge} ${styles[variant] || ""}`}>
      {children}
    </span>
  );
};

const EmptyCollection = ({ label }) => {
  return (
    <div className={styles.empty}>
      {t(label)}
    </div>
  );
};

const ContactCard = ({ contact }) => {
  const type = contact?.contactType ?? contact?.contact_type ?? t("person.contact");
  const value = contact?.contactValue ?? contact?.contact_value ?? "";
  const verifiedAt = contact?.verifiedAt ?? contact?.verified_at;

  return (
    <article className={styles.itemCard}>
      <div className={styles.itemHeader}>
        <div className={styles.itemTitle}>
          {type}
        </div>

        {isPrimary(contact) ? (
          <Badge variant="success">{t("common.primary")}</Badge>
        ) : null}
      </div>

      <div className={styles.itemMain}>
        {value || t("common.empty")}
      </div>

      <div className={styles.itemMeta}>
        {verifiedAt ? `${t("common.verifiedAt")}: ${formatDate(verifiedAt)}` : t("common.notVerified")}
      </div>
    </article>
  );
};

const ResidenceCard = ({ residence }) => {
  const department =
    residence?.departmentCode ??
    residence?.department_code ??
    "";

  const commune =
    residence?.communeCode ??
    residence?.commune_code ??
    "";

  const section =
    residence?.sectionCode ??
    residence?.section_code ??
    "";

  const validFrom =
    residence?.validFrom ??
    residence?.valid_from ??
    "";

  const validTo =
    residence?.validTo ??
    residence?.valid_to ??
    "";

  const parts = [
    department !== "" ? `${t("person.department")}: ${department}` : null,
    commune !== "" ? `${t("person.commune")}: ${commune}` : null,
    section !== "" ? `${t("person.section")}: ${section}` : null,
    validFrom ? `${t("person.validFrom")}: ${formatDate(validFrom)}` : null,
    validTo ? `${t("person.validTo")}: ${formatDate(validTo)}` : null,
  ].filter(Boolean);

  return (
    <article className={styles.itemCard}>
      <div className={styles.itemHeader}>
        <div className={styles.itemTitle}>
          {t("person.residence")}
        </div>

        {isPrimary(residence) ? (
          <Badge variant="success">{t("common.primary")}</Badge>
        ) : null}
      </div>

      <div className={styles.itemMain}>
        {residence?.address || t("common.noAddress")}
      </div>

      {parts.length ? (
        <div className={styles.itemMeta}>
          {parts.join(" · ")}
        </div>
      ) : null}
    </article>
  );
};

const PhotoCard = ({ photo, person, canReadPhoto }) => {
  const src = canReadPhoto ? photoToAvatarSrc(photo) : "";

  return (
    <article className={styles.photoCard}>
      {src ? (
        <img
          src={src}
          alt={fullName(person) || t("person.photoAlt")}
          className={styles.photoImage}
        />
      ) : (
        <Avatar
          variant="rounded"
          sx={{ width: 92, height: 92, fontSize: 24, fontWeight: 800 }}
        >
          {initials(person?.firstName, person?.lastName)}
        </Avatar>
      )}

      <div className={styles.photoMeta}>
        <div className={styles.itemHeader}>
          <div className={styles.itemTitle}>
            {t("common.photo")}
          </div>

          {isDefaultPhoto(photo) ? (
            <Badge variant="success">{t("common.default")}</Badge>
          ) : null}
        </div>

        {photo?.capturedAt || photo?.captured_at ? (
          <div className={styles.itemMeta}>
            {t("common.captured")}: {formatDate(photo.capturedAt ?? photo.captured_at)}
          </div>
        ) : null}

        {photo?.mimeType || photo?.mime_type ? (
          <div className={styles.itemMeta}>
            {t("common.type")}: {photo.mimeType ?? photo.mime_type}
          </div>
        ) : null}

        {photo?.objectKey || photo?.object_key ? (
          <div className={styles.itemMeta}>
            {t("common.objectKey")}: {photo.objectKey ?? photo.object_key}
          </div>
        ) : null}
      </div>
    </article>
  );
};

const VoterCard = ({ voter }) => {
  const votingCenterGid =
    voter?.votingCenterGid ??
    voter?.voting_center_gid ??
    "";

  return (
    <article className={styles.itemCard}>
      <div className={styles.itemTitle}>
        {t("voter.voter")}
      </div>

      <div className={styles.itemMain}>
        {t("person.gid")}: {voter?.gid || ""}
      </div>

      <div className={styles.itemMeta}>
        {t("person.personGid")}: {voter?.personGid ?? voter?.person_gid ?? ""}
        {votingCenterGid ? ` · ${t("voter.votingCenter")}: ${votingCenterGid}` : ""}
      </div>
    </article>
  );
};

const ApplicationCard = ({ application }) => {
  const status =
    application?.statusCode ??
    application?.status_code ??
    "";

  const receipt =
    application?.receiptNumber ??
    application?.receipt_number ??
    "";

  const submitted =
    application?.submittedAt ??
    application?.submitted_at;

  const votingCenterGid =
    application?.votingCenterGid ??
    application?.voting_center_gid ??
    "";

  return (
    <article className={styles.itemCard}>
      <div className={styles.itemTitle}>
        {t("voter.application")}
      </div>

      <div className={styles.itemMain}>
        {t("voter.receipt")}: {receipt || t("common.noReceipt")}
      </div>

      <div className={styles.itemMeta}>
        {t("common.status")}: {status || ""}
        {submitted ? ` · ${t("common.submitted")}: ${formatDateTime(submitted)}` : ""}
        {votingCenterGid ? ` · ${t("voter.votingCenter")}: ${votingCenterGid}` : ""}
      </div>
    </article>
  );
};

const PersonHeader = ({ data }) => {
  const name = fullName(data);
  const avatarSrc = data?.avatarSrc || "";
  const headerRegistrationLabel = data?.headerRegistrationLabel || "";
  const registrationStatus = data?.registrationStatusCode || "";

  return (
    <section className={styles.header}>
      <div className={styles.headerIdentity}>
        {avatarSrc ? (
          <img
            src={avatarSrc}
            alt={name || t("person.pictureAlt")}
            className={styles.headerImage}
          />
        ) : (
          <Avatar
            sx={{ width: 96, height: 96, fontSize: 34, fontWeight: 800 }}
          >
            {initials(data?.firstName, data?.lastName)}
          </Avatar>
        )}

        <div className={styles.headerText}>
          <h1>{name || t("person.unnamed")}</h1>

          {data?.nationalIdentifier ? (
            <div className={styles.headerSubtext}>
              {t("person.nationalId")}: {data.nationalIdentifier}
            </div>
          ) : null}
        </div>
      </div>

      <div className={styles.headerBadges}>
        {registrationStatus ? (
          <Chip
            label={registrationStatus}
            color={registrationStatus === "ACTIVE" ? "success" : "default"}
            variant="outlined"
          />
        ) : null}

        {headerRegistrationLabel ? (
          <Badge variant="success">
            {headerRegistrationLabel}
          </Badge>
        ) : null}
      </div>
    </section>
  );
};

const PersonReadView = ({ gui, id }) => {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setError("");

        const person = await searchOneByGid("person", id);
        const enriched = await enrichPerson(person, gui);

        if (!cancelled) {
          setData(enriched);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || String(err));
          setData(null);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [gui, id]);

  if (error) {
    return (
      <div className={styles.error}>
        {error}
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div className={styles.page}>
      <PersonHeader data={data} />

      <div className={styles.gridIdentityPhotos}>
        <SectionCard title={t("person.identity")}>
          <div className={styles.fieldsGrid}>
            <Field label={t("person.firstName")} value={data.firstName} />
            <Field label={t("person.lastName")} value={data.lastName} />
            <Field label={t("person.birthDate")} value={formatDate(data.dateOfBirth)} />
            <Field label={t("person.sex")} value={data.sexCode} />
            <Field label={t("person.nationalIdentifier")} value={data.nationalIdentifier} />
            <Field label={t("person.officeIdentifierNumber")} value={data.officeIdentifierNumber} />
            <Field label={t("person.recordNumber")} value={data.recordNumber} />
            <Field label={t("person.registrationDate")} value={formatDate(data.registrationDate)} />
            <Field label={t("person.registrationStatus")} value={data.registrationStatusCode} />
          </div>
        </SectionCard>

        {can(gui, "SEARCH_PERSON_PHOTO") ? (
          <SectionCard title={t("person.photos")}>
            <div className={styles.collection}>
              {(data.photos || []).length ? (
                data.photos.map((photo, index) => (
                  <PhotoCard
                    key={photo?.id || photo?.gid || index}
                    photo={photo}
                    person={data}
                    canReadPhoto={can(gui, "READ_PERSON_PHOTO")}
                  />
                ))
              ) : (
                <EmptyCollection label="person.noPhotos" />
              )}
            </div>
          </SectionCard>
        ) : null}
      </div>

      <div className={styles.gridApplicationsVoters}>
        {can(gui, "SEARCH_VOTER_APPLICATION") ? (
          <SectionCard title={t("person.applications")}>
            <div className={styles.collection}>
              {(data.applications || []).length ? (
                data.applications.map((application, index) => (
                  <ApplicationCard
                    key={application?.id || application?.gid || index}
                    application={application}
                  />
                ))
              ) : (
                <EmptyCollection label="person.noApplications" />
              )}
            </div>
          </SectionCard>
        ) : null}

        {can(gui, "SEARCH_VOTER") ? (
          <SectionCard title={t("person.voters")}>
            <div className={styles.collection}>
              {(data.voters || []).length ? (
                data.voters.map((voter, index) => (
                  <VoterCard
                    key={voter?.id || voter?.gid || index}
                    voter={voter}
                  />
                ))
              ) : (
                <EmptyCollection label="person.noVoters" />
              )}
            </div>
          </SectionCard>
        ) : null}
      </div>

      <div className={styles.gridContactsResidences}>
        {can(gui, "SEARCH_PERSON_CONTACT") ? (
          <SectionCard title={t("person.contacts")}>
            <div className={styles.collection}>
              {(data.contacts || []).length ? (
                data.contacts.map((contact, index) => (
                  <ContactCard
                    key={contact?.id || contact?.gid || index}
                    contact={contact}
                  />
                ))
              ) : (
                <EmptyCollection label="person.noContacts" />
              )}
            </div>
          </SectionCard>
        ) : null}

        {can(gui, "SEARCH_PERSON_RESIDENCE") ? (
          <SectionCard title={t("person.residences")}>
            <div className={styles.collection}>
              {(data.residences || []).length ? (
                data.residences.map((residence, index) => (
                  <ResidenceCard
                    key={residence?.id || residence?.gid || index}
                    residence={residence}
                  />
                ))
              ) : (
                <EmptyCollection label="person.noResidences" />
              )}
            </div>
          </SectionCard>
        ) : null}
      </div>
    </div>
  );
};

export const personProjection = {
  sections: [
    {
      title: "person.identity",
      fields: [
        { name: "firstName", label: "person.firstName", valueType: "text", inputType: "text", span: 6 },
        { name: "lastName", label: "person.lastName", valueType: "text", inputType: "text", span: 6 },
        { name: "dateOfBirth", label: "person.birthDate", valueType: "date", inputType: "date", span: 6 },
        { name: "sexCode", label: "person.sex", valueType: "text", inputType: "text", span: 6 },
        { name: "nationalIdentifier", label: "person.nationalIdentifier", valueType: "text", inputType: "text", span: 6 },
        { name: "officeIdentifierNumber", label: "person.officeIdentifierNumber", valueType: "text", inputType: "text", span: 6 },
        { name: "recordNumber", label: "person.recordNumber", valueType: "text", inputType: "text", span: 6 },
        { name: "registrationDate", label: "person.registrationDate", valueType: "date", inputType: "date", span: 6 },
        { name: "registrationStatusCode", label: "person.registrationStatus", valueType: "text", inputType: "text", span: 6 },
      ],
    },
  ],
};

export const displayReadPerson = (gui, id) => {
  return {
    uuid: `person-read-${id}`,
    view: () => <PersonReadView gui={gui} id={id} />,
  };
};

export const displayEditPerson = (gui, id, onFinish) => {
  return displayEdit({
    gui,
    projection: personProjection,

    loadData: async () => {
      if (!id) {
        return buildEmptyObject(personProjection);
      }

      const person = await searchOneByGid("person", id);
      return person || {};
    },

    onSubmit: async (values) => {
      const payload = { ...values };

      delete payload.fullName;
      delete payload.headerRegistrationLabel;

      delete payload.contacts;
      delete payload.residences;
      delete payload.photos;
      delete payload.voters;
      delete payload.applications;

      delete payload.primaryPhoto;
      delete payload.avatarSrc;

      const response = await rest.request(
        getServiceUri() + (id ? "person/update-document" : "person/new-document"),
        "POST",
        payload
      );

      if (onFinish) {
        onFinish(response);
      }

      return response;
    },
  });
};